import { useState, useEffect, useCallback, useContext, useRef } from "react";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { adminContext } from "../../Common/PageRoute";
import { FileDownloadStatus } from "../../Utilities/ApiUtility.axios";
import InvoiceExtractionService, {
  IInvoiceAdminListItem,
  IInvoiceAdminItemKey,
} from "../../Services/InvoiceExtractionService";
import { IIdNameOption } from "../../Services/XcelifyExtractionService";
import { usePractice } from "../../Context/PracticeContext";

dayjs.extend(customParseFormat);

export interface IExtractionProgressState {
  active: boolean;
  total: number;
  processed: number;
  percent: number;
}

interface ISavedJobSession {
  jobId: string;
  totalCount: number;
  startedAt: number;
}

const LOCAL_STORAGE_JOB_KEY = "invoice_active_extraction_job";

const loadSavedJobSession = (): ISavedJobSession | null => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_JOB_KEY);
    if (!raw) return null;
    const data: ISavedJobSession = JSON.parse(raw);
    if (!data?.jobId || Date.now() - data.startedAt > 30 * 60 * 1000) {
      localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
      return null;
    }
    return data;
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
    return null;
  }
};

const saveJobSession = (jobId: string, totalCount: number) => {
  try {
    const session: ISavedJobSession = { jobId, totalCount, startedAt: Date.now() };
    localStorage.setItem(LOCAL_STORAGE_JOB_KEY, JSON.stringify(session));
  } catch {}
};

const clearSavedJobSession = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
  } catch {}
};

export function useInvoiceExtraction() {
  const { openNotification } = useContext(adminContext);
  const { allPracticeIds } = usePractice();

  const practiceMap: Record<number, string> = {};
  allPracticeIds?.forEach((p: any) => {
    practiceMap[p.value] = p.label;
  });

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const [companyList, setCompanyList] = useState<IIdNameOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const [selectedPracticeId, setSelectedPracticeId] = useState<number | undefined>(undefined);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [fromDate, setFromDate] = useState<Dayjs | null>(dayjs().startOf("month"));
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs().endOf("day"));

  // Pagination states matching Ant Design Table pattern
  const [items, setItems] = useState<IInvoiceAdminListItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshToggle, setRefreshToggle] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItems, setSelectedItems] = useState<IInvoiceAdminItemKey[]>([]);

  const [extractionProgress, setExtractionProgress] = useState<IExtractionProgressState>({
    active: false,
    total: 0,
    processed: 0,
    percent: 0,
  });

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const downloadAbortControllerRef = useRef<AbortController | null>(null);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const resetProgressUI = useCallback(() => {
    stopPolling();
    setDownloading(false);
    setActiveJobId(null);
    clearSavedJobSession();
    setTimeout(() => {
      setExtractionProgress({ active: false, total: 0, processed: 0, percent: 0 });
    }, 3000);
  }, [stopPolling]);

  // Restore saved job session on mount
  useEffect(() => {
    const saved = loadSavedJobSession();
    if (!saved) return;

    setDownloading(true);
    setActiveJobId(saved.jobId);
    setExtractionProgress({ active: true, total: saved.totalCount, processed: 0, percent: 0 });

    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const prog = await InvoiceExtractionService.getProgress(saved.jobId);
        if (prog && prog.status !== "Not Found") {
          setExtractionProgress((prev) => ({
            active: true,
            total: prog.total || saved.totalCount || prev.total,
            processed: prog.processed ?? prev.processed,
            percent: prog.percent ?? prev.percent,
          }));

          const isTerminal =
            (prog.status === "Completed" && prog.processed >= prog.total && prog.total > 0) ||
            prog.status === "Failed" ||
            prog.status === "Cancelled";

          if (isTerminal) {
            resetProgressUI();
          }
        }
      } catch {}
    }, 1000);

    return () => stopPolling();
  }, [stopPolling, resetProgressUI]);

  // Default Practice selection as soon as allPracticeIds are ready
  useEffect(() => {
    if (allPracticeIds?.length > 0 && selectedPracticeId === undefined) {
      setSelectedPracticeId(allPracticeIds[0].value);
    }
  }, [allPracticeIds, selectedPracticeId]);

  // Load Companies when Practice changes
  useEffect(() => {
    const pId = selectedPracticeId;
    if (!pId || pId <= 0) {
      setCompanyList([]);
      setSelectedClientId(undefined);
      return;
    }

    let isMounted = true;
    setLoadingCompanies(true);
    InvoiceExtractionService.getCompaniesByPractice(pId)
      .then((res) => {
        if (isMounted) setCompanyList(res || []);
      })
      .catch((err) => {
        console.error("Error loading practice companies:", err);
        if (isMounted) setCompanyList([]);
      })
      .finally(() => {
        if (isMounted) setLoadingCompanies(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedPracticeId]);

  // Fetch Invoice records
  const fetchRecords = useCallback(async () => {
    const pId = selectedPracticeId ?? (allPracticeIds?.length > 0 ? allPracticeIds[0].value : undefined);
    if (!pId) {
      setItems([]);
      setTotalItems(0);
      return;
    }

    setLoading(true);
    try {
      const start = (currentPage - 1) * pageSize;
      const fromStr = fromDate ? fromDate.format("DD/MM/YYYY") : undefined;
      const toStr = toDate ? toDate.format("DD/MM/YYYY") : undefined;

      const res = await InvoiceExtractionService.getList(
        start,
        pageSize,
        searchQuery,
        pId,
        fromStr,
        toStr,
        selectedClientId,
        statusFilter
      );

      if (res) {
        setItems(res.items || []);
        setTotalItems(res.totalRecords || 0);
      } else {
        openNotification("error", "Failed to fetch invoice records");
      }
    } catch (err: any) {
      openNotification("error", err?.message || "Error fetching invoice records");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    searchQuery,
    selectedPracticeId,
    allPracticeIds,
    fromDate,
    toDate,
    selectedClientId,
    statusFilter,
    openNotification,
  ]);

  // Trigger fetch when dependencies or refreshToggle change
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, refreshToggle]);

  const handlePracticeChange = useCallback((value?: number) => {
    setSelectedPracticeId(value);
    setSelectedClientId(undefined);
    setStatusFilter(undefined);
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setCurrentPage(1);
  }, []);

  const handleCompanyChange = useCallback((value?: string) => {
    setSelectedClientId(value);
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((status?: string) => {
    setStatusFilter(status);
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setCurrentPage(1);
  }, []);

  const handleDateRangeChange = useCallback((from: Dayjs | null, to: Dayjs | null) => {
    setFromDate(from);
    setToDate(to);
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback((val: string) => {
    setSearchQuery(val);
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setCurrentPage(1);
  }, []);

  const handleRefresh = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedItems([]);
    setRefreshToggle((t) => !t);
  }, []);

  const handleRowSelectionChange = useCallback(
    (keys: React.Key[], selectedRows: IInvoiceAdminListItem[]) => {
      setSelectedRowKeys(keys);
      setSelectedItems(
        selectedRows.map((r) => ({ id: r.id, practiceId: r.practiceId }))
      );
    },
    []
  );

  const handleSingleDownload = useCallback(
    async (record: IInvoiceAdminListItem) => {
      setDownloadingId(record.id);
      try {
        openNotification("info", `Downloading invoice ${record.number}...`);
        const res = await InvoiceExtractionService.downloadSingle(
          record.id,
          record.practiceId
        );
        if (res?.status === FileDownloadStatus.error) {
          openNotification(
            "error",
            "Download failed. File not found or inaccessible."
          );
        }
      } catch (err: any) {
        openNotification("error", err?.message || "Failed to download single file.");
      } finally {
        setDownloadingId(null);
      }
    },
    [openNotification]
  );

  const handleCancelExtraction = useCallback(async () => {
    stopPolling();

    if (downloadAbortControllerRef.current) {
      downloadAbortControllerRef.current.abort();
      downloadAbortControllerRef.current = null;
    }

    const jobIdToCancel = activeJobId || loadSavedJobSession()?.jobId;

    clearSavedJobSession();
    setDownloading(false);
    setActiveJobId(null);
    setExtractionProgress({ active: false, total: 0, processed: 0, percent: 0 });

    if (jobIdToCancel) {
      try {
        await InvoiceExtractionService.cancelProgress(jobIdToCancel);
      } catch (e) {
        console.error("Cancel API error:", e);
      }
    }

    openNotification("warning", "Extraction job cancelled by user.");
  }, [activeJobId, stopPolling, openNotification]);

  const handleBulkExtraction = useCallback(
    async (extractAll: boolean = false) => {
      if (!extractAll && selectedItems.length === 0) {
        openNotification("warning", "Please select at least one record to extract.");
        return;
      }

      const count = extractAll ? totalItems : selectedItems.length;
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      const abortController = new AbortController();
      downloadAbortControllerRef.current = abortController;

      saveJobSession(jobId, count);
      setDownloading(true);
      setActiveJobId(jobId);
      setExtractionProgress({ active: true, total: count, processed: 0, percent: 0 });

      stopPolling();
      pollTimerRef.current = setInterval(async () => {
        try {
          const prog = await InvoiceExtractionService.getProgress(jobId);
          if (prog) {
            setExtractionProgress((prev) => ({
              active: true,
              total: prog.total || count || prev.total,
              processed: prog.processed ?? prev.processed,
              percent: prog.percent ?? prev.percent,
            }));

            const isTerminal =
              (prog.status === "Completed" &&
                prog.processed >= prog.total &&
                prog.total > 0) ||
              prog.status === "Failed" ||
              prog.status === "Cancelled";

            if (isTerminal) {
              resetProgressUI();
            }
          }
        } catch {}
      }, 1000);

      try {
        openNotification("info", `Streaming ${count} invoice file(s) into zip package...`);
        const pId = selectedPracticeId || 0;
        const fromIso = fromDate
          ? fromDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss")
          : undefined;
        const toIso = toDate
          ? toDate.endOf("day").format("YYYY-MM-DDTHH:mm:ss")
          : undefined;

        const res = await InvoiceExtractionService.extractBulk(
          extractAll ? [] : selectedItems,
          extractAll,
          pId,
          fromIso,
          toIso,
          selectedClientId,
          statusFilter,
          searchQuery,
          jobId,
          abortController.signal
        );
        if (res?.status === FileDownloadStatus.error) {
          if (!abortController.signal.aborted) {
            openNotification("error", "Extraction failed. Files could not be retrieved.");
          }
        }
      } catch (err: any) {
        if (err?.name !== "AbortError" && err?.code !== "ERR_CANCELED") {
          openNotification("error", err?.message || "Failed to extract bulk files.");
        }
      } finally {
        downloadAbortControllerRef.current = null;
        resetProgressUI();
      }
    },
    [
      selectedItems,
      totalItems,
      selectedPracticeId,
      fromDate,
      toDate,
      selectedClientId,
      statusFilter,
      searchQuery,
      stopPolling,
      resetProgressUI,
      openNotification,
    ]
  );

  return {
    loading,
    downloading,
    downloadingId,
    activeJobId,
    allPracticeIds,
    practiceMap,
    companyList,
    loadingCompanies,
    selectedPracticeId,
    selectedClientId,
    statusFilter,
    fromDate,
    toDate,
    searchQuery,
    items,
    totalItems,
    currentPage,
    pageSize,
    selectedRowKeys,
    selectedItems,
    extractionProgress,
    setCurrentPage,
    setPageSize,
    fetchRecords,
    handlePracticeChange,
    handleCompanyChange,
    handleStatusChange,
    handleDateRangeChange,
    handleSearch,
    handleRefresh,
    handleRowSelectionChange,
    handleSingleDownload,
    handleBulkExtraction,
    handleCancelExtraction,
  };
}
